import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Printer, Download, ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Print-optimised full-page renderer for the chosen announcement card.
 *
 * Behaviour:
 * - Loads the memorial + the chosen (or latest) announcement card image.
 * - Screen view: dark stage + preview + "Print" and "Save to device"
 *   controls (hidden when printed).
 * - Print view: single A5 / 5×7in page with the card image dead-centre,
 *   no header, no navigation, no scrollbars. Configured via ``@page`` in
 *   a scoped ``<style>`` tag.
 *
 * URL: /celebrate/:slug/announcement-print
 */
export default function CelebrateAnnouncementPrint() {
  const { slug } = useParams();
  const [memorial, setMemorial] = useState(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [size, setSize] = useState("A5"); // A5 | 5x7 | 4x6 | LETTER
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      http.get(`/memorials/${slug}`),
      http.get(`/memorials/${slug}/announcement-cards`),
    ])
      .then(([mRes, cRes]) => {
        if (!alive) return;
        const m = mRes.data;
        setMemorial(m);
        const cards = cRes.data?.items || [];
        const chosenId = cRes.data?.chosen_id;
        const chosen = cards.find((c) => c.id === chosenId) || cards[cards.length - 1];
        if (!chosen) {
          setError("No announcement card has been generated yet for this memorial.");
        } else {
          setCard(chosen);
        }
      })
      .catch((e) => alive && setError(e?.response?.data?.detail || "Could not load memorial"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const cardSrc = card ? (card.url?.startsWith("http") || card.url?.startsWith("data:") ? card.url : `${backend}${card.url}`) : null;

  const doPrint = () => window.print();

  const doDownload = async () => {
    if (!card) return;
    setDownloading(true);
    try {
      if (card.storage_path && card.id) {
        // Server route sets Content-Disposition: attachment
        const url = `${backend}/api/memorials/${memorial.slug || memorial.id}/announcement-card/${card.id}/download`;
        // Trigger download via anchor
        const a = document.createElement("a");
        a.href = url;
        a.rel = "noopener";
        a.download = `announcement-${memorial.slug}-${card.id}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Downloading…");
      } else if (cardSrc?.startsWith("data:")) {
        // Direct base64 → save via anchor
        const a = document.createElement("a");
        a.href = cardSrc;
        a.download = `announcement-${memorial.slug || "card"}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Downloaded to your device");
      } else if (cardSrc) {
        // Fallback: fetch and save via blob
        const res = await fetch(cardSrc);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `announcement-${memorial.slug || "card"}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Downloaded to your device");
      }
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  // Paper-size specs (width × height in mm)
  const PAPER = {
    A5: { css: "A5", label: "A5 (148 × 210 mm)" },
    "5x7": { css: "5in 7in", label: "5 × 7 inches" },
    "4x6": { css: "4in 6in", label: "4 × 6 inches" },
    LETTER: { css: "letter portrait", label: "US Letter" },
  };
  const paperCss = PAPER[size]?.css || "A5";

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="announcement-print-page">
      {/* Scoped print styles */}
      <style>{`
        @page { size: ${paperCss}; margin: 0; }
        @media print {
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .print-hide { display: none !important; }
          .print-stage { background: white !important; padding: 0 !important; }
          .print-card {
            width: 100vw !important;
            height: 100vh !important;
            max-width: none !important;
            max-height: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            object-fit: contain !important;
          }
        }
      `}</style>

      {/* Screen controls */}
      <div className="print-hide sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to={`/celebrate/${slug}`} className="inline-flex items-center gap-2 text-sm opacity-80 hover:opacity-100" data-testid="print-back">
            <ArrowLeft className="w-4 h-4" /> Back to memorial
          </Link>
          {memorial && (
            <div className="text-sm opacity-70">
              · <span className="font-medium">{memorial.full_name}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[11px] font-mono uppercase tracking-wider opacity-70">Paper</label>
          <select value={size} onChange={(e) => setSize(e.target.value)} className="rounded border border-white/20 bg-black/40 px-2 py-1 text-xs" data-testid="print-paper-select">
            {Object.entries(PAPER).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button
            onClick={doDownload}
            disabled={!card || downloading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm border border-white/20"
            data-testid="print-download-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Save to device
          </button>
          <button
            onClick={doPrint}
            disabled={!card}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white text-black hover:bg-white/90 text-sm font-medium"
            data-testid="print-print-btn"
          >
            <Printer className="w-4 h-4" /> Print card
          </button>
        </div>
      </div>

      {/* Stage */}
      <div className="print-stage flex-1 flex items-center justify-center p-6">
        {loading ? (
          <div className="text-center opacity-70"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…</div>
        ) : error ? (
          <div className="text-center max-w-md">
            <div className="text-lg font-medium">Nothing to print yet</div>
            <p className="text-sm opacity-70 mt-2">{error}</p>
            <Link to={`/celebrate/${slug}`} className="inline-flex items-center gap-2 mt-4 wofy-btn-primary">
              <ArrowLeft className="w-4 h-4" /> Back to memorial
            </Link>
          </div>
        ) : cardSrc ? (
          <img
            src={cardSrc}
            alt={`Death announcement for ${memorial?.full_name}`}
            className="print-card shadow-2xl rounded-md max-h-[85vh]"
            data-testid="print-card-image"
          />
        ) : null}
      </div>

      {/* Footnote */}
      {!loading && !error && (
        <div className="print-hide px-6 pb-6 text-center text-[11px] opacity-60">
          Tip: choose <b>Save as PDF</b> in the print dialog to keep a digital copy · or hit
          <b> Save to device</b> for the raw JPG.
        </div>
      )}
    </div>
  );
}
